import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faShoppingBag, faGlobe, faSpinner } from "@fortawesome/free-solid-svg-icons";

import { useCartStore } from "../../store/CartStore";
import { useNonUserCartStore } from "../../store/NonUserCartStore";
import useAuthStore from "../../store/AuthStore";

import GuestCheckoutForm from "./GuestCheckoutForm";
import CountryValidation from "./CountryValidation";
import { paymentService } from "../../services";
import supportedCountries from "../../assets/countryLists";

const CartDrawer = ({ onClose }) => {
  const { user } = useAuthStore();
  const { cartItems, clearCart, removeFromCart } = user
    ? useCartStore()
    : useNonUserCartStore();

  const [showGuestForm, setShowGuestForm] = useState(false);
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [removingItemId, setRemovingItemId] = useState(null); // Track which item is being removed
  const [error, setError] = useState("");

  const activeItems =
    cartItems?.filter((item) => item.asset?.status !== "SOLD") || [];

  const totalAmount = activeItems.reduce(
    (sum, item) => sum + (item.asset?.price || 0),
    0
  );

  /* ----------------------------- helpers ----------------------------- */

  const resolveCountryForUser = (countryName) =>
    supportedCountries.find(
      (c) => c.name.toLowerCase() === countryName?.toLowerCase()
    );

  const buildPaymentPayload = ({ email, phone, country }) => ({
    assets: activeItems.map((item) => item.asset?._id),
    email,
    phone,
    country: country.name,
  });

  const initPayment = async (payload) => {
    try {
      setCheckingOut(true);
      setError("");

      const response = await paymentService.initPayment(payload);

      if (response?.link) {
        window.location.href = response.link;
      }

      if (response?.success) {
        clearCart();
      }
    } catch (err) {
      setError(err.message || "Payment initialization failed");
      setCheckingOut(false);
    }
  };

  /* ----------------------------- checkout flows ----------------------------- */

  const handleCheckoutClick = () => {
    if (!user) {
      setShowGuestForm(true);
      return;
    }

    const resolvedCountry = resolveCountryForUser(user.country);

    if (!resolvedCountry) {
      setShowCountryPicker(true);
      return;
    }

    initPayment(
      buildPaymentPayload({
        email: user.email,
        phone: user.phone,
        country: resolvedCountry,
      })
    );
  };

  const handleGuestCheckout = (formData) => {
    initPayment(buildPaymentPayload(formData));
  };

  const handleAuthenticatedCountrySelect = (country) => {
    setShowCountryPicker(false);

    initPayment(
      buildPaymentPayload({
        email: user.email,
        phone: user.phone,
        country,
      })
    );
  };

  const handleRemove = async (assetId) => {
    setRemovingItemId(assetId); // Show removing state
    try {
      await removeFromCart(assetId); // Ensure removeFromCart returns a promise
    } catch (err) {
      setError(err.message || "Failed to remove item");
    } finally {
      setRemovingItemId(null);
    }
  };

  /* ----------------------------- UI ----------------------------- */

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ duration: 0.3 }}
      className="fixed top-0 right-0 w-full max-w-md h-full min-h-[99vh] bg-gradient-to-b from-gray-900 via-gray-950 to-black text-white z-50 border-l border-purple-500/30 overflow-y-auto"
    >
      {/* Header */}
      <div className="flex justify-between items-center px-4 py-3 bg-gradient-to-r from-purple-500 to-purple-600">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <FontAwesomeIcon icon={faShoppingBag} />
          Cart Summary
        </h2>
        <button onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Country Picker for Auth Users */}
        {showCountryPicker && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <CountryValidation
              value={null}
              onChange={handleAuthenticatedCountrySelect}
            />
          </div>
        )}

        {!cartItems || cartItems.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            Your cart is empty.
          </div>
        ) : (
          <>
            <AnimatePresence>
              {cartItems.map((item) => (
                <motion.div
                  key={item._id}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 50 }}
                  transition={{ duration: 0.3 }}
                  className="p-4 rounded-xl bg-gray-900 border border-purple-500/40 flex justify-between items-center"
                >
                  <div>
                    <h4 className="font-semibold">{item.title}</h4>
                    <p className="text-sm text-gray-400 flex items-center gap-2">
                      <FontAwesomeIcon icon={faGlobe} />
                      {item.asset?.title}
                    </p>
                    <p className="text-purple-400 mt-1">
                      ${item.asset?.price?.toFixed(2)}
                    </p>
                  </div>

                  <button
                    onClick={() => handleRemove(item.asset?._id)}
                    disabled={removingItemId === item.asset?._id}
                    className={`flex items-center justify-center px-3 py-1 rounded-lg border ${
                      removingItemId === item.asset?._id
                        ? "border-gray-500 text-gray-400 cursor-not-allowed"
                        : "border-red-400 text-red-400"
                    }`}
                  >
                    {removingItemId === item.asset?._id ? (
                      <>
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="mr-2"
                        />
                        Removing...
                      </>
                    ) : (
                      "Remove"
                    )}
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>

            <div className="pt-4 border-t border-gray-700">
              <p className="text-center text-purple-400 font-semibold">
                Total: ${totalAmount.toFixed(2)}
              </p>

              {!showGuestForm ? (
                <button
                  onClick={handleCheckoutClick}
                  disabled={checkingOut}
                  className="w-full mt-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-purple-600"
                >
                  {checkingOut ? "Processing..." : "Proceed to Checkout"}
                </button>
              ) : (
                <GuestCheckoutForm onSubmit={handleGuestCheckout} />
              )}
            </div>
          </>
        )}

        {error && <p className="text-red-400 text-center text-sm">{error}</p>}
      </div>
    </motion.div>
  );
};

export default CartDrawer;